import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import { SourcesButton } from 'ai-components-ui-library/components/buttons/SourcesButton';
import { SourcesList } from 'ai-components-ui-library/components/informative/sources/SourcesList';
import { CopyIcon } from 'foundations-assets/icons/components/CopyIcon';
import { MessagesIcon } from 'foundations-assets/icons/components/MessagesIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { AccordionTransition } from 'foundations-components/transitional/components/AccordionTransition';
import { Box } from 'foundations-components/transitional/components/Box';
import { CopyButton } from 'foundations-components/transitional/components/CopyButton';
import { Popover, PopoverBody, PopoverHeader } from 'foundations-components/transitional/components/Popover';
import { ScrollContainer } from 'foundations-components/transitional/components/ScrollContainer';
import { useCallback, useContext, useMemo, useState } from 'react';
import { ChatMessageSourceDataTypename } from 'chatspot-core/__generated__/chirp/com/hubspot/copilot/rpc/v2/CopilotMessageService';
import { CrmCitationTypename, WebPageCitationTypename, DashworksCitationTypename } from '../../types/Callbacks';
import { SectionContext } from '../../context/SectionContext';
const SOURCE_TYPE_TO_CITATION_TYPENAME = {
  CRM_OBJECT: CrmCitationTypename,
  WEBSITE: WebPageCitationTypename,
  APP: DashworksCitationTypename
};
import { CitationsIcon, StyledIconHolderWrapper } from '../citations/CitationsIcon';
import MemorySourceIcon from './MemorySourceIcon';
import { formatSourcesForCopy } from './formatSourcesForCopy';
import { getLocationWithinDocument } from './getLocationWithinDocument';
import { getSourceType } from './getSourceType';
import { getSourceUrl } from './getSourceUrl';
import { isMemorySource } from './memorySourcesUtils';
import { cleanSourceText } from './sourceTextUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const getSourceIcon = source => {
  var _source$data, _source$iconUrl;
  if (isMemorySource(source)) {
    return /*#__PURE__*/_jsx(MemorySourceIcon, {});
  }
  if (((_source$data = source.data) === null || _source$data === void 0 ? void 0 : _source$data.__typename) === ChatMessageSourceDataTypename) {
    return /*#__PURE__*/_jsx(StyledIconHolderWrapper, {
      Icon: MessagesIcon
    });
  }
  const sourceType = getSourceType(source);
  if (!sourceType) {
    return null;
  }
  return /*#__PURE__*/_jsx(CitationsIcon, {
    sourceType: sourceType,
    data: source.data,
    iconSrc: (_source$iconUrl = source.iconUrl) !== null && _source$iconUrl !== void 0 ? _source$iconUrl : undefined
  });
};
const ExpandableSourcesList = ({
  sourcesSections,
  expandable = false,
  expanded = false,
  onSourceClick,
  onSourceOpen,
  handledCitationTypes,
  popoverTitle,
  onSourcesOpened,
  onSourcesCopied
}) => {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const {
    usageTracker
  } = useContext(SectionContext);
  const handleSourceClick = source => {
    var _source$data2;
    // Memory and chat-message sources have no URL to open — navigation is handled entirely by the consumer via onSourceClick.
    if (isMemorySource(source) || ((_source$data2 = source.data) === null || _source$data2 === void 0 ? void 0 : _source$data2.__typename) === ChatMessageSourceDataTypename) {
      onSourceClick === null || onSourceClick === void 0 || onSourceClick(source);
      return;
    }
    const sourceType = getSourceType(source);
    const citationTypename = sourceType ? SOURCE_TYPE_TO_CITATION_TYPENAME[sourceType] : undefined;
    const shouldOverride = onSourceOpen && (!(handledCitationTypes !== null && handledCitationTypes !== void 0 && handledCitationTypes.length) || !!citationTypename && handledCitationTypes.includes(citationTypename));
    if (shouldOverride) {
      onSourceOpen(source);
    } else {
      const url = getSourceUrl(source);
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    }
    onSourceClick === null || onSourceClick === void 0 || onSourceClick(source);
  };
  const handleSourcesButtonClick = () => {
    if (expandable) {
      setIsExpanded(!isExpanded);
    } else {
      onSourcesOpened === null || onSourcesOpened === void 0 || onSourcesOpened();
      setIsPopoverOpen(true);
    }
  };
  const copyValue = useMemo(() => formatSourcesForCopy(sourcesSections), [sourcesSections]);
  const handleCopyClick = useCallback(() => {
    onSourcesCopied === null || onSourcesCopied === void 0 || onSourcesCopied();
    if (usageTracker) {
      usageTracker.track('copyMessageInteraction', {
        action: 'sources_copied'
      });
    }
  }, [onSourcesCopied, usageTracker]);
  const copyButton = /*#__PURE__*/_jsx(Flex, {
    inlineSize: "fit-content",
    children: /*#__PURE__*/_jsxs(CopyButton, {
      value: copyValue,
      size: "xs",
      onClick: handleCopyClick,
      children: [/*#__PURE__*/_jsx(CopyIcon, {
        size: "300"
      }), /*#__PURE__*/_jsx(FormattedMessage, {
        message: "chatspotComponents.sections.sourcesListSectionFormatter.copyAllSources"
      })]
    })
  });
  const sourcesListContent = sourcesSections.map(sourcesSection => /*#__PURE__*/_jsx(SourcesList.Section, {
    title: sourcesSection.title || undefined,
    children: sourcesSection.sources.map(source => {
      var _source$data3, _source$data4;
      const sourceIcon = getSourceIcon(source);
      const locationText = getLocationWithinDocument(source);
      return /*#__PURE__*/_jsx(SourcesList.Source, {
        primaryText: cleanSourceText(source.primaryText, (_source$data3 = source.data) === null || _source$data3 === void 0 ? void 0 : _source$data3.source),
        secondaryText: cleanSourceText(source.secondaryText, (_source$data4 = source.data) === null || _source$data4 === void 0 ? void 0 : _source$data4.source),
        locationText: locationText,
        icon: sourceIcon,
        data: source,
        onSourceClick: () => handleSourceClick(source)
      }, source.id);
    })
  }, sourcesSection.title || 'untitled'));
  const sourcesList = /*#__PURE__*/_jsx(SourcesList, {
    footer: copyButton,
    children: sourcesListContent
  });
  const sourcesListWithoutFooter = /*#__PURE__*/_jsx(SourcesList, {
    children: sourcesListContent
  });
  const getIconsForButton = () => {
    if (sourcesSections.length <= 1) {
      // If only one section, show first icon
      const icons = sourcesSections.flatMap(sourcesSection => sourcesSection.sources.map(source => getSourceIcon(source))).filter(Boolean);
      return icons[0] ? [icons[0]] : [];
    }

    // If multiple sections, pick one representative icon from each section
    return sourcesSections.map(sourcesSection => {
      // Find the first source in this section that has an icon
      const sourceWithIcon = sourcesSection.sources.find(source => {
        const icon = getSourceIcon(source);
        return icon !== null;
      });
      return sourceWithIcon ? getSourceIcon(sourceWithIcon) : null;
    }).filter(Boolean);
  };
  const sourcesListButton = /*#__PURE__*/_jsx(SourcesButton, {
    onClick: handleSourcesButtonClick,
    selected: isExpanded,
    icons: getIconsForButton(),
    count: sourcesSections.flatMap(sourcesSection => sourcesSection.sources.length).reduce((a, b) => a + b, 0)
  });
  if (expandable) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(Flex, {
        justifyContent: "start",
        marginBlock: "200",
        children: /*#__PURE__*/_jsx(Box, {
          children: sourcesListButton
        })
      }), /*#__PURE__*/_jsx(AccordionTransition, {
        open: isExpanded,
        children: sourcesList
      })]
    });
  }
  return /*#__PURE__*/_jsx(Popover, {
    closeOnOutsideClick: true,
    borderRadius: "300",
    margin: "200",
    minInlineSize: "360px",
    maxInlineSize: "400px",
    placement: "top right",
    content: /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(PopoverHeader, {
        paddingBlockStart: "400",
        paddingInline: "400",
        showCloseButton: true,
        onOpenChange: () => setIsPopoverOpen(false),
        children: /*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          children: popoverTitle || unescapedText('chatspotComponents.sections.sourcesListSectionFormatter.title')
        })
      }), /*#__PURE__*/_jsx(PopoverBody, {
        paddingBlock: "300",
        paddingInline: "400",
        children: /*#__PURE__*/_jsxs(Flex, {
          flexDirection: "column",
          gap: "200",
          children: [/*#__PURE__*/_jsx(ScrollContainer, {
            maxHeight: "360px",
            children: sourcesListWithoutFooter
          }), /*#__PURE__*/_jsx(Flex, {
            paddingBlockStart: "200",
            paddingBlockEnd: "100",
            children: copyButton
          })]
        })
      })]
    }),
    open: isPopoverOpen,
    onOpenChange: () => setIsPopoverOpen(false),
    children: sourcesListButton
  });
};
export default ExpandableSourcesList;